<template>
  <div>
    <div
      class="p-3 flex flex-col gap-2 bg-pure-white border rounded"
      :class="[
        activeProspectStage ? 'border-light-purple' : 'border-slate-400',
        dragId === item.id ? 'opacity-0' : 'opacity-100',
        item.is_admitted ? 'cursor-pointer' : 'cursor-grab'
      ]"
      @click="openPanel"
      @mousedown="$emit('drag-start', { e: $event, prospect_stage: item })"
      @touchstart="$emit('drag-start', { e: $event, prospect_stage: item })"
      :data-id="item.id"
    >
      <div class="flex" :title="item.prospect.name">
        <BlTextSubtitle2 :text="item.prospect.name" class="truncate" />
      </div>
      <div
        v-if="item.upcoming_activities_count"
        class="flex items-center gap-2"
      >
        <BlIcon icon="list-check" color="slate-6" class="w-4 h-4" />
        <div class="px-1.5 h-5.5 flex items-center bg-lime-100 rounded">
          <BlTextBody2
            :text="`${item.upcoming_activities_count} upcoming to-do(s)`"
            class="text-lime-700"
          />
        </div>
      </div>
      <div class="relative flex items-center gap-2">
        <BlIcon icon="tag" color="slate-6" class="w-4 h-4" />
        <div v-if="!tags.length">
          <BlTextBody2 text="No tags" class="italic" />
        </div>
        <div v-else class="flex items-center gap-2 truncate">
          <div
            v-for="tag in tags.slice(0, 3)"
            :key="tag.id"
            class="px-1.5 h-5.5 min-w-12 flex items-center justify-center rounded truncate"
            :class="{ 'bg-slate-300': !tag.background_color }"
            :style="{ backgroundColor: tag.background_color || 'unset' }"
            :title="tag.name"
          >
            <BlTextBody2
              :text="tag.name"
              :style="{ color: tag.color || 'unset' }"
              class="truncate"
            />
          </div>
          <div
            v-if="tags.slice(3).length"
            class="flex flex-shrink-0 items-center justify-center cursor-pointer"
            @click="openMoreTags = !openMoreTags"
          >
            <BlBubbleCount
              type="secondary"
              :count="`+ ${tags.slice(3).length}`"
              class="h-7 hover:bg-slate-400 text-slate-700 bg-slate-300"
            />
          </div>
        </div>
        <div
          v-if="openMoreTags"
          class="absolute z-50 top-8 right-0 p-1 gap-1 flex flex-col min-w-40 bg-pure-white rounded border border-slate-400 truncate"
        >
          <div
            v-for="tag in tags.slice(3)"
            :key="tag.id"
            class="px-4 py-2 flex text-left rounded truncate max-w-48"
            :title="tag.name"
          >
            <BlTextBody2 class="truncate" :text="tag.name" />
          </div>
        </div>
      </div>
      <div class="relative flex items-center gap-2">
        <BlIcon icon="user" color="slate-6" class="w-4 h-4" />
        <div v-if="!assignedUsers.length">
          <BlTextBody2 text="No assigned users" class="italic" />
        </div>
        <div v-else class="flex items-center gap-2 truncate">
          <div
            v-for="user in assignedUsers.slice(0, 3)"
            :key="user.id"
            class="px-1.5 h-5.5 flex items-center bg-slate-300 rounded truncate"
            :title="user.name"
          >
            <BlTextBody2 :text="user.name" class="truncate" />
          </div>
          <div
            v-if="assignedUsers.slice(3).length"
            class="flex items-center justify-center cursor-pointer"
            @click="openMoreUsers = !openMoreUsers"
          >
            <BlBubbleCount
              type="secondary"
              :count="`+ ${assignedUsers.slice(3).length}`"
              class="h-7 hover:bg-slate-400 text-slate-700 bg-slate-300"
            />
          </div>
        </div>
        <div
          v-if="openMoreUsers"
          class="absolute z-50 top-8 right-0 p-1 gap-1 flex flex-col min-w-40 bg-pure-white rounded border border-slate-400 truncate"
        >
          <div
            v-for="user in assignedUsers.slice(3)"
            :key="user.id"
            class="px-4 py-2 flex text-left rounded truncate max-w-48"
            :title="user.name"
          >
            <BlTextBody2 class="truncate" :text="user.name" />
          </div>
        </div>
      </div>
    </div>

    <ProspectPanel
      v-if="item.id"
      location="kanban"
      :open="panelOpened"
      :id="item.id"
      @close-panel="panelOpened = false"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import ProspectPanel from "../modal/ProspectPanel.vue";

import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlBubbleCount from "../../BlBubbleCount.vue";

export default {
  name: "PipelineProspectStage",
  components: {
    ProspectPanel,
    BlTextSubtitle2,
    BlTextBody2,
    BlIcon,
    BlBubbleCount
  },
  props: {
    item: { type: Object, required: true },
    dragId: { type: Number, default: 0 }
    // @drag-start
  },
  data() {
    return {
      openMoreTags: false,
      openMoreUsers: false,
      panelOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "prospectStage"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    activeProspectStage() {
      if (this.panelOpened) return true;
      if (this.prospectStage && this.prospectStage.id === this.item.id) {
        return true;
      }
      return false;
    },
    tags() {
      const arr = this.item.prospect.prospect_tags || [];
      if (this.enableBranch && this.item.prospect.branch) {
        return [this.item.prospect.branch, ...arr];
      }
      return arr;
    },
    assignedUsers() {
      return this.item.assigned_users || [];
    }
  },
  watch: {
    openMoreTags(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerMoreTags);
        }, 10);
      }
    },
    openMoreUsers(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerMoreUsers);
        }, 10);
      }
    }
  },
  mounted() {},
  methods: {
    openPanel() {
      this.panelOpened = true;
    },
    closeHandlerMoreTags() {
      window.removeEventListener("click", this.closeHandlerMoreTags);
      this.openMoreTags = false;
    },
    closeHandlerMoreUsers() {
      window.removeEventListener("click", this.closeHandlerMoreUsers);
      this.openMoreUsers = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/pipeline/ProspectStage.vue